import { ThemeNameOrFunction } from 'app/shared/hoc/withTheme';

export interface RouteInfo {
  path: string;
  iframeOnly?: boolean;
  componentPath: string;
  require?: string[];
  postAuthReturnToPath?: string;
  permission?: string;
  permissionContexts?: string[];
  // Only list flags that are globally enabled/disabled via Flipper with no actor needed.
  // Do NOT list experiment flags (handled by useExperimentFeatureFlags) or actor-specific
  // flags (handled by useFeatureFlagsForFlipperId) - those must be loaded separately.
  featureFlagsToLoad?: string[];
  themeName: ThemeNameOrFunction;
  localeMessages?: string;
}

const requiresForAdmin = [
  'auth',
  'permissions',
  'userAgreements',
  'organizerSurvey',
  'canSuppressCookieBot',
];

// Please keep in alphabetical order by path, where possible and it makes sense
export const routesConfig: RouteInfo[] = [
  {
    path: '/',
    componentPath: 'app/fan/components/pages/Home',
    require: ['braze'], // TODO: requiring braze on the home page is only temporary and is for testing purposes
    themeName: 'common',
    localeMessages: 'fan',
    featureFlagsToLoad: ['gift_cards_entrypoints'],
  },
  {
    path: '/api-auth-setup/:authSetupToken',
    componentPath: 'app/fan/components/pages/ApiAuthSetup',
    themeName: 'common',
    localeMessages: 'fan',
  },
  {
    path: '/api-docs/v3/:docToken',
    componentPath: 'app/fan/components/pages/ApiDocumentation',
    themeName: 'common',
    localeMessages: 'fan',
  },
  {
    path: '/sms-sign-up',
    componentPath: 'app/fan/components/pages/SmsSignUp',
    themeName: 'common',
    localeMessages: 'fan',
  },
  {
    path: '/email-sms-sign-up',
    componentPath: 'app/fan/components/pages/KlaviyoEmailSmsEmbeddedForm',
    themeName: 'common',
    localeMessages: 'fan',
  },
  {
    path: '/about/artists',
    componentPath: 'app/artist/components/pages/ArtistApplyInfo',
    themeName: 'manualCSS',
    localeMessages: 'prismic',
  },
  {
    path: '/about/artists/apply',
    componentPath: 'app/artist/components/pages/ArtistApplication',
    require: ['auth'],
    themeName: 'manualCSS',
    localeMessages: 'artist',
  },
  {
    path: '/about/artists/how-it-works',
    componentPath: 'app/artist/components/pages/ArtistsHowItWorks',
    themeName: 'manualCSS',
    localeMessages: 'prismic',
  },
  {
    path: '/about/artists/how-it-works-playing',
    componentPath: 'app/artist/components/pages/ArtistsHowItWorksPlaying',
    themeName: 'manualCSS',
    localeMessages: 'prismic',
  },
  {
    path: '/about/artists/how-it-works-artist-compensation',
    componentPath:
      'app/artist/components/pages/ArtistsHowItWorksArtistCompensation',
    themeName: 'manualCSS',
    localeMessages: 'prismic',
  },
  {
    path: '/about/artists/how-it-works-video',
    componentPath: 'app/artist/components/pages/ArtistsHowItWorksVideo',
    themeName: 'manualCSS',
    localeMessages: 'prismic',
  },
  {
    path: '/about/hosts',
    componentPath: 'app/venue/components/pages/VenueApplyInfo',
    themeName: 'manualCSS',
    localeMessages: 'prismic',
  },
  {
    path: '/about/hosts/apply',
    componentPath: 'app/venue/components/pages/VenueApplication',
    themeName: 'common',
    localeMessages: 'venue',
  },
  {
    path: '/admin/artist-applications',
    componentPath: 'app/admin/components/pages/AdminArtistApplications',
    require: requiresForAdmin.concat('hotjar'),
    permission: 'artistApplication.view',
    permissionContexts: ['artist', 'artistApplication'],
    themeName: 'adminManualCSS',
  },
  {
    path: '/admin/artists',
    componentPath: 'app/admin/components/pages/AdminArtists',
    require: requiresForAdmin.concat('hotjar'),
    permission: 'artist.list.view',
    permissionContexts: ['artist', 'artistOffer', 'user'],
    themeName: 'adminManualCSS',
  },
  {
    path: '/admin/artist-booking-invitations',
    componentPath: 'app/admin/components/pages/AdminOffers',
    require: requiresForAdmin.concat('hotjar'),
    permission: 'artistOffer.list.view',
    permissionContexts: ['artistOffer'],
    themeName: 'adminManualCSS',
  },
  {
    path: '/admin/artist-merge',
    componentPath: 'app/admin/components/pages/AdminArtistMerge',
    require: requiresForAdmin,
    permission: 'artist.merge',
    permissionContexts: ['artist'],
    themeName: 'adminManualCSS',
  },
  {
    path: '/admin/booking-requests',
    componentPath: 'app/admin/components/pages/AdminBookingRequests',
    require: requiresForAdmin.concat('hotjar'),
    permission: 'artist.bookingRequestList.view',
    permissionContexts: ['artist'],
    themeName: 'adminManualCSS',
  },
  {
    path: '/admin/broadcasts',
    componentPath: 'app/admin/components/pages/AdminBroadcasts',
    require: requiresForAdmin,
    permission: 'event.broadcast.access',
    permissionContexts: ['event'],
    themeName: 'adminManualCSS',
  },
  {
    path: '/admin/business-owners',
    componentPath: 'app/admin/components/pages/AdminBusinessOwners',
    require: requiresForAdmin,
    permission: 'businessOwner.list.view',
    permissionContexts: ['businessOwner'],
    themeName: 'adminManualCSS',
  },
  {
    path: '/admin/campaigns',
    componentPath: 'app/admin/components/pages/AdminCampaigns',
    require: requiresForAdmin,
    permission: 'campaign.access',
    permissionContexts: ['campaign', 'user', 'promoCode'],
    themeName: 'adminManualCSS',
  },
  {
    // TODO: Change this to /admin/cities and add permanent redirect
    // /admin/city-directory to /admin/cities
    path: '/admin/city-directory',
    componentPath: 'app/admin/components/pages/AdminCities',
    require: requiresForAdmin,
    permission: 'city.list.view',
    permissionContexts: ['city', 'neighborhood', 'user'],
    themeName: 'adminManualCSS',
  },
  {
    path: '/admin/collections',
    componentPath: 'app/admin/components/pages/AdminCollections',
    require: requiresForAdmin.concat('hotjar'),
    permission: 'eventCollection.access',
    permissionContexts: ['event', 'eventCollection'],
    themeName: 'adminManualCSS',
  },
  {
    path: '/admin/competitions',
    componentPath: 'app/admin/components/pages/AdminCompetitions',
    require: requiresForAdmin,
    permission: 'competition.access',
    permissionContexts: ['competition'],
    themeName: 'adminManualCSS',
  },
  {
    path: '/admin/event-planner',
    componentPath: 'app/admin/components/pages/AdminEventPlanner',
    require: requiresForAdmin.concat('hotjar'),
    permission: 'event.list.view',
    permissionContexts: [
      'artistOffer',
      'campaign',
      'city',
      'competition',
      'event',
      'eventCollection',
      'partner',
      'role',
      'visaPresale',
    ],
    themeName: 'adminManualCSS',
  },
  {
    path: '/admin/event-planner/:showFieldsParam',
    componentPath: 'app/admin/components/pages/AdminEventPlanner',
    require: requiresForAdmin.concat('hotjar'),
    permission: 'event.list.view',
    permissionContexts: ['event'],
    themeName: 'adminManualCSS',
  },
  {
    path: '/admin',
    componentPath: 'app/admin/components/pages/AdminMainPortal',
    require: requiresForAdmin,
    themeName: 'adminManualCSS',
  },
  {
    path: '/admin/city-revenue',
    componentPath: 'app/admin/components/pages/AdminCityRevenueItems',
    require: requiresForAdmin,
    permission: 'cityRevenueItem.view',
    permissionContexts: ['cityRevenueItem'],
    themeName: 'adminManualCSS',
  },
  {
    path: '/admin/countries',
    componentPath: 'app/admin/components/pages/AdminCountries',
    require: requiresForAdmin,
    permission: 'country.access',
    permissionContexts: ['country', 'continent'],
    themeName: 'adminManualCSS',
  },
  {
    path: '/admin/crew-directory',
    componentPath: 'app/admin/components/pages/AdminCrew',
    require: requiresForAdmin,
    permission: 'crew.access',
    permissionContexts: ['crew', 'event', 'role'],
    themeName: 'adminManualCSS',
  },
  {
    path: '/admin/crew-portal/events/:eventId',
    componentPath: 'app/admin/components/pages/AdminCrewEvent',
    require: requiresForAdmin.concat('hotjar'),
    permission: 'event.crew.access',
    permissionContexts: ['artist', 'crew', 'event', 'role', 'venue'],
    themeName: 'adminManualCSS',
  },
  {
    path: '/admin/crew-portal',
    componentPath: 'app/admin/components/pages/AdminCrewPortal',
    require: requiresForAdmin.concat('hotjar'),
    permission: 'event.crew.access',
    permissionContexts: ['artist', 'crew', 'event', 'role', 'venue'],
    themeName: 'adminManualCSS',
  },
  {
    path: '/admin/confidentiality-agreement',
    componentPath: 'app/admin/components/pages/AdminUserAgreement',
    require: ['auth'],
    themeName: 'adminManualCSS',
  },
  {
    path: '/admin/curator-agreement',
    componentPath: 'app/admin/components/pages/AdminUserAgreement',
    require: ['auth'],
    themeName: 'adminManualCSS',
  },
  {
    path: '/admin/curator-agreement-2',
    componentPath: 'app/admin/components/pages/AdminUserAgreement',
    require: ['auth'],
    themeName: 'adminManualCSS',
  },
  {
    path: '/admin/curator-agreement-3',
    componentPath: 'app/admin/components/pages/AdminUserAgreement',
    require: ['auth'],
    themeName: 'adminManualCSS',
  },
  {
    path: '/admin/organizer-groups',
    componentPath: 'app/admin/components/pages/AdminOrganizerGroups',
    require: requiresForAdmin,
    permission: 'organizerGroup.list.view',
    permissionContexts: ['organizerGroup'],
    themeName: 'adminManualCSS',
  },
  {
    path: '/admin/email-testing',
    componentPath: 'app/admin/components/pages/AdminEmailTest',
    require: requiresForAdmin,
    permission: 'email.emailTest.access',
    permissionContexts: ['email'],
    themeName: 'adminManualCSS',
  },
  {
    path: '/admin/emails',
    componentPath: 'app/admin/components/pages/AdminEmailMessages',
    require: requiresForAdmin,
    permission: 'emailMessage.access',
    permissionContexts: ['emailMessage'],
    themeName: 'adminManualCSS',
  },
  {
    path: '/admin/gdpr-data-removal-form',
    componentPath: 'app/admin/components/pages/AdminGdprDataRemovalForm',
    require: requiresForAdmin,
    permission: 'user.gdprDataRemovalProcess.create',
    permissionContexts: ['user'],
    themeName: 'adminManualCSS',
  },
  {
    path: '/admin/custom-email-templates',
    componentPath: 'app/admin/components/pages/AdminCustomEmailTemplates',
    require: requiresForAdmin,
    permission: 'event.list.accessBasic',
    permissionContexts: ['event'],
    themeName: 'adminManualCSS',
  },
  {
    path: '/admin/dashboards-curator',
    componentPath: 'app/admin/components/pages/AdminDashboards',
    require: requiresForAdmin,
    permission: 'metabaseDashboard.curator.view',
    themeName: 'adminManualCSS',
  },
  {
    path: '/admin/dashboards-marketing',
    componentPath: 'app/admin/components/pages/AdminDashboards',
    require: requiresForAdmin.concat('hotjar'),
    permission: 'metabaseDashboard.marketing.view',
    themeName: 'adminManualCSS',
  },
  {
    path: '/admin/dashboards-operations',
    componentPath: 'app/admin/components/pages/AdminDashboards',
    require: requiresForAdmin.concat('hotjar'),
    permission: 'metabaseDashboard.operations.view',
    themeName: 'adminManualCSS',
  },
  {
    path: '/admin/dashboards-performance',
    componentPath: 'app/admin/components/pages/AdminDashboards',
    require: requiresForAdmin.concat('hotjar'),
    permission: 'metabaseDashboard.performance.view',
    themeName: 'adminManualCSS',
  },
  {
    path: '/admin/dashboards-producer',
    componentPath: 'app/admin/components/pages/AdminDashboards',
    require: requiresForAdmin,
    permission: 'metabaseDashboard.producer.view',
    themeName: 'adminManualCSS',
  },
  {
    path: '/admin/internal-users',
    componentPath: 'app/admin/components/pages/AdminInternalUsers',
    require: requiresForAdmin,
    permission: 'user.internalList.view',
    permissionContexts: ['user'],
    themeName: 'adminManualCSS',
  },
  {
    path: '/admin/add-internal-users',
    componentPath: 'app/admin/components/pages/AdminCreateUsers',
    require: requiresForAdmin,
    permission: 'user.bulkCreate',
    permissionContexts: ['user'],
    themeName: 'adminManualCSS',
  },
  {
    path: '/admin/event-financials',
    componentPath: 'app/admin/components/pages/AdminEventFinancials',
    require: requiresForAdmin,
    permission: 'event.detailedFinancials.view',
    permissionContexts: ['event'],
    themeName: 'adminManualCSS',
  },
  {
    path: '/admin/gift-cards',
    componentPath: 'app/admin/components/pages/AdminCustomerCredits',
    require: requiresForAdmin,
    permission: 'customerCredit.access',
    permissionContexts: ['customerCredit'],
    themeName: 'adminManualCSS',
  },
  {
    path: '/admin/merchandising-details',
    componentPath: 'app/admin/components/pages/AdminMerchandisingDetails',
    require: requiresForAdmin.concat('hotjar'),
    permission: 'property.edit',
    permissionContexts: ['property'],
    themeName: 'adminManualCSS',
  },
  {
    path: '/admin/partners',
    componentPath: 'app/admin/components/pages/AdminPartners',
    require: requiresForAdmin,
    permission: 'partner.access',
    permissionContexts: ['partner', 'user'],
    themeName: 'adminManualCSS',
  },
  {
    path: '/admin/print-day-of-show-guestlist',
    componentPath: 'app/admin/components/pages/AdminPrintGuestlistDayOfShow',
    require: requiresForAdmin,
    permission: 'event.guestlist.access',
    permissionContexts: ['event'],
    themeName: 'adminManualCSS',
  },
  {
    path: '/admin/promo-codes',
    componentPath: 'app/admin/components/pages/AdminPromoCodes',
    require: requiresForAdmin.concat('hotjar'),
    permission: 'promoCode.access',
    permissionContexts: ['promoCode'],
    themeName: 'adminManualCSS',
  },
  {
    path: '/admin/resources',
    componentPath: 'app/admin/components/pages/AdminResources',
    require: requiresForAdmin.concat('hotjar'),
    permission: 'resources.list.view',
    themeName: 'adminManualCSS',
  },
  {
    path: '/admin/roles-and-permissions',
    componentPath: 'app/admin/components/pages/AdminRolesAndPermissions',
    require: requiresForAdmin,
    permission: 'permission.list.view',
    permissionContexts: ['permission'],
    themeName: 'adminManualCSS',
  },
  {
    path: '/admin/site-banner',
    componentPath: 'app/admin/components/pages/AdminSiteBanner',
    require: requiresForAdmin,
    permission: 'setting.global.edit',
    permissionContexts: ['setting'],
    themeName: 'adminManualCSS',
  },
  {
    path: '/admin/themes',
    componentPath: 'app/admin/components/pages/AdminThemes',
    require: requiresForAdmin,
    permission: 'theme.view',
    permissionContexts: ['theme'],
    themeName: 'adminManualCSS',
  },
  {
    path: '/admin/users',
    componentPath: 'app/admin/components/pages/AdminUsers',
    require: requiresForAdmin,
    permission: 'user.list.access',
    permissionContexts: ['user', 'event'],
    themeName: 'adminManualCSS',
  },
  {
    path: '/admin/venue-applications',
    componentPath: 'app/admin/components/pages/AdminVenueApplications',
    require: requiresForAdmin.concat('hotjar'),
    permission: 'venueApplication.view',
    permissionContexts: ['venue', 'venueApplication'],
    themeName: 'adminManualCSS',
  },
  {
    path: '/admin/venues',
    componentPath: 'app/admin/components/pages/AdminVenues',
    require: requiresForAdmin.concat('hotjar'),
    permission: 'venue.list.view',
    permissionContexts: ['venue'],
    themeName: 'adminManualCSS',
  },
  {
    path: '/admin/versions',
    componentPath: 'app/admin/components/pages/AdminVersions',
    require: requiresForAdmin,
    permission: 'tool.versionHistory.access',
    permissionContexts: ['tool'],
    themeName: 'adminManualCSS',
  },
  {
    path: '/admin/versions/:id',
    componentPath: 'app/admin/components/pages/AdminVersion',
    require: requiresForAdmin,
    permission: 'tool.versionHistory.access',
    permissionContexts: ['tool'],
    themeName: 'adminManualCSS',
  },
  {
    path: '/artists/:slug',
    componentPath: 'app/fan/components/pages/ArtistProfile',
    themeName: 'common',
    localeMessages: 'fan',
  },
  {
    path: '/artist/booking-invitation/:id',
    componentPath: 'app/artist/components/pages/ArtistOfferDetails',
    require: ['auth'],
    // We set permissionContexts here to trigger loading of permissions from backend, but we don't
    // require a permission check or set a page-wide permission to check b/c instead of using the
    // built-in page-wide permissions check, we manually check for permissions in the page code
    // since we have more complex logic checking for EITHER has a permission
    // OR is associated with given artist
    permissionContexts: ['artist'],
    themeName: 'adminManualCSS',
    localeMessages: 'artist',
  },
  {
    path: '/artist/dashboard',
    componentPath: 'app/artist/components/pages/ArtistDashboardHome',
    require: ['auth'],
    postAuthReturnToPath: '/artist/dashboard',
    themeName: 'adminManualCSS',
    localeMessages: 'artist',
  },
  {
    path: '/artist/edit-profile',
    componentPath: 'app/artist/components/pages/ArtistProfileEdit',
    require: ['auth'],
    postAuthReturnToPath: '/artist/edit-profile',
    themeName: 'adminManualCSS',
    localeMessages: 'artist',
  },
  {
    path: '/artist/event/:id',
    componentPath: 'app/artist/components/pages/ArtistEventDetails',
    require: ['auth'],
    // We set permissionContexts here to trigger loading of permissions from backend, but we don't
    // require a permission check or set a page-wide permission to check b/c instead of using the
    // built-in page-wide permissions check, we manually check for permissions in the page code
    // since we have more complex logic checking for EITHER has a permission
    // OR is associated with given artist
    permissionContexts: ['artist'],
    themeName: 'adminManualCSS',
    localeMessages: 'artist',
  },
  {
    path: '/artist/fans',
    componentPath: 'app/artist/components/pages/ArtistDashboardFans',
    require: ['auth'],
    postAuthReturnToPath: '/artist/fans',
    themeName: 'adminManualCSS',
    localeMessages: 'artist',
  },
  {
    path: '/artist/featured-set/:id',
    componentPath: 'app/artist/components/pages/ArtistFeaturedSetDetails',
    require: ['auth'],
    themeName: 'adminManualCSS',
    localeMessages: 'artist',
  },
  {
    path: '/artist/network',
    componentPath: 'app/artist/components/pages/ArtistDashboardNetwork',
    require: ['auth'],
    postAuthReturnToPath: '/artist/network',
    themeName: 'adminManualCSS',
    localeMessages: 'artist',
  },
  {
    path: '/artist/resources',
    componentPath: 'app/artist/components/pages/ArtistDashboardResources',
    require: ['auth'],
    postAuthReturnToPath: '/artist/resources',
    themeName: 'adminManualCSS',
    localeMessages: 'artist',
  },
  {
    path: '/artist/shows',
    componentPath: 'app/artist/components/pages/ArtistDashboardShows',
    require: ['auth'],
    postAuthReturnToPath: '/artist/shows',
    themeName: 'adminManualCSS',
    localeMessages: 'artist',
  },
  {
    path: '/attendee-details/:eventId',
    componentPath: 'app/fan/components/pages/AttendeeDetailsEdit',
    themeName: 'common',
    localeMessages: 'fan',
    require: ['auth'],
  },
  {
    path: '/auth/secret/new',
    componentPath: 'app/fan/components/pages/PasswordRequest',
    themeName: 'common',
    localeMessages: 'fan',
  },
  {
    path: '/auth/secret/edit',
    componentPath: 'app/fan/components/pages/PasswordReset',
    themeName: 'common',
    localeMessages: 'fan',
  },
  {
    path: '/checkout/:eventId',
    componentPath: 'app/fan/components/pages/Checkout',
    // We set permissionContexts here to trigger loading of permissions from backend, but we don't
    // require a permission check or set a page-wide permission to check b/c instead of using the
    // built-in page-wide permissions check, we manually check for permissions in the page code
    // since we need permissions only in certain cases, instead of requiring permissions for
    // the entire page
    permissionContexts: ['visaPresale'],
    themeName: 'common',
    localeMessages: 'fan',
  },
  {
    path: '/events/:eventId',
    componentPath: 'app/fan/components/pages/Event',
    // We set permissionContexts here to trigger loading of permissions from backend, but we don't
    // require a permission check or set a page-wide permission to check b/c instead of using the
    // built-in page-wide permissions check, we manually check for permissions in the page code
    // since we need permissions only in certain cases, instead of requiring permissions for
    // the entire page
    permissionContexts: ['city', 'event', 'visaPresale'],
    themeName: 'common',
    localeMessages: 'fan',
    featureFlagsToLoad: ['reveal_commercial_venue_addresses'],
  },
  {
    path: '/cities/:citySlug',
    componentPath: 'app/fan/components/pages/City',
    require: ['hotjar'],
    // We set permissionContexts here to trigger loading of permissions from backend, but we don't
    // require a permission check or set a page-wide permission to check b/c instead of using the
    // built-in page-wide permissions check, we manually check for permissions in the page code
    // since we need permissions only in certain cases, instead of requiring permissions for
    // the entire page
    permissionContexts: ['city', 'event', 'visaPresale'],
    themeName: 'common',
    localeMessages: 'fan',
    featureFlagsToLoad: ['gift_cards_entrypoints'],
  },
  {
    path: '/cities/:citySlug/lists/:collectionSlug',
    componentPath: 'app/fan/components/pages/Collection',
    require: ['hotjar'],
    permissionContexts: ['eventCollection'],
    themeName: 'common',
    localeMessages: 'fan',
  },
  {
    path: '/:citySlug/today',
    componentPath: 'app/fan/components/pages/TodaysEvents',
    themeName: 'common',
    localeMessages: 'fan',
  },
  {
    path: '/community-guidelines',
    componentPath: 'app/fan/components/pages/CommunityGuidelines',
    themeName: 'common',
    localeMessages: 'fan',
  },
  {
    path: '/comp/:slug',
    componentPath: 'app/fan/components/pages/Competition',
    themeName: 'common',
    localeMessages: 'prismic',
  },
  {
    path: '/confirm-venue-details/:uuid',
    componentPath: 'app/venue/components/pages/ConfirmVenueDetails',
    require: ['auth'],
    themeName: 'manualCSS',
    localeMessages: 'venue',
  },
  {
    path: '/dashboard',
    componentPath: 'app/fan/components/pages/Dashboard',
    require: ['auth'],
    // We set permissionContexts here to trigger loading of permissions from backend, but we don't
    // require a permission check or set a page-wide permission to check b/c instead of using the
    // built-in page-wide permissions check, we manually check for permissions in the page code
    // since we need permissions only in certain cases, instead of requiring permissions for
    // the entire page
    permissionContexts: ['visaPresale'],
    themeName: 'common',
    localeMessages: 'fan',
  },
  {
    path: '/featuring/:slug',
    componentPath: 'app/fan/components/pages/FeaturedSet',
    // We set permissionContexts here to trigger loading of permissions from backend, but we don't
    // require a permission check or set a page-wide permission to check b/c instead of using the
    // built-in page-wide permissions check, we manually check for permissions in the page code
    // since we need permissions only in certain cases, instead of requiring permissions for
    // the entire page
    permissionContexts: ['event'],
    themeName: 'common',
  },
  {
    path: '/clp/:variant/cities/:citySlug',
    componentPath: 'app/fan/components/pages/LandingPageWithCity',
    themeName: 'common',
    localeMessages: 'fan',
  },
  {
    path: '/clp/:variant/cities/:citySlug/lists/:collectionSlug',
    componentPath: 'app/fan/components/pages/LandingPageWithCity',
    themeName: 'common',
    localeMessages: 'fan',
  },
  {
    path: '/clp/:variant/lists/:collectionSlug',
    componentPath: 'app/fan/components/pages/LandingPageNoCity',
    themeName: 'common',
    localeMessages: 'fan',
  },
  {
    path: '/clp/:variant/:slug',
    componentPath: 'app/fan/components/pages/LandingPageNoCity',
    themeName: 'common',
    localeMessages: 'fan',
  },
  {
    path: '/giftcards',
    componentPath: 'app/fan/components/pages/GiftCards',
    themeName: 'common',
    localeMessages: 'fan',
    featureFlagsToLoad: ['gift_card_design_contest'],
  },
  {
    path: '/giftcards/checkout',
    componentPath: 'app/fan/components/pages/GiftCardCheckout',
    themeName: 'common',
    localeMessages: 'fan',
  },
  {
    path: '/giftcards/check-balance',
    componentPath: 'app/fan/components/pages/GiftCardCheckBalance',
    themeName: 'common',
    localeMessages: 'fan',
  },
  {
    path: '/host-invitations/:id',
    componentPath: 'app/venue/components/pages/VenueOffer',
    require: ['auth'],
    // We set permissionContexts here to trigger loading of permissions from backend, but we don't
    // require a permission check or set a page-wide permission to check b/c instead of using the
    // built-in page-wide permissions check, we manually check for permissions in the page code
    // since we need permissions only in certain cases, instead of requiring permissions for
    // the entire page
    permissionContexts: ['venue'],
    themeName: 'manualCSS',
    localeMessages: 'venue',
  },
  {
    path: '/lists/:collectionSlug',
    componentPath: 'app/fan/components/pages/Collection',
    require: ['hotjar'],
    permissionContexts: ['eventCollection'],
    themeName: 'common',
    localeMessages: 'fan',
  },
  {
    path: '/lists/subscribe',
    componentPath: 'app/fan/components/pages/FollowEventCollection',
    themeName: 'common',
    localeMessages: 'fan',
  },
  {
    path: '/lists/unsubscribe',
    componentPath: 'app/fan/components/pages/UnfollowEventCollection',
    themeName: 'common',
    localeMessages: 'fan',
  },
  {
    path: '/mention-me',
    componentPath: 'app/fan/components/pages/MentionMe',
    themeName: 'common',
    localeMessages: 'fan',
  },
  {
    path: '/o2-live-competition',
    componentPath: 'app/fan/components/pages/O2LiveCompetition',
    themeName: 'common',
    localeMessages: 'fan',
  },
  {
    path: '/privacy-preferences/:referenceName',
    componentPath: 'app/fan/components/pages/PrivacyPreferences',
    themeName: 'common',
    localeMessages: 'fan',
  },
  {
    path: '/profile/edit',
    componentPath: 'app/fan/components/pages/Profile',
    require: ['auth'],
    localeMessages: 'fan',
    themeName: 'common',
  },
  {
    path: '/sofar-series',
    componentPath: 'app/fan/components/pages/SofarSeries',
    themeName: 'common',
  },
  {
    path: '/sofar-series/have-i-made-it',
    componentPath: 'app/fan/components/pages/SofarSeriesHaveIMadeIt',
    themeName: 'common',
  },
  {
    path: '/about/artists/request-artist',
    componentPath: 'app/artist/components/pages/ArtistAccessRequest',
    themeName: 'manualCSS',
    localeMessages: 'artist',
  },
  {
    path: '/set-up-artist-access',
    componentPath: 'app/artist/components/pages/ArtistAccessSetUp',
    require: ['auth'],
    themeName: 'manualCSS',
    localeMessages: 'artist',
  },
  {
    path: '/claim_account',
    componentPath: 'app/fan/components/pages/ClaimGuestUserAccount',
    themeName: 'common',
    localeMessages: 'fan',
  },
  {
    path: '/signin',
    componentPath: 'app/fan/components/pages/SignIn',
    themeName: 'common',
    localeMessages: 'fan',
  },
  {
    path: '/signup',
    componentPath: 'app/fan/components/pages/SignIn',
    themeName: 'common',
    localeMessages: 'fan',
  },
  {
    path: '/v/artistresources',
    componentPath: 'app/artist/components/pages/ArtistResources',
    themeName: 'manualCSS',
    localeMessages: 'prismic',
  },
  {
    path: '/venue-community-host-assurance-promise',
    componentPath:
      'app/venue/components/pages/VenueCommunityHostAssurancePromise',
    require: ['auth'],
    postAuthReturnToPath: '/venue-terms-and-conditions',
    themeName: 'manualCSS',
    localeMessages: 'prismic',
  },
  {
    path: '/venue-terms-and-conditions',
    componentPath: 'app/venue/components/pages/VenueTermsAndConditions',
    require: ['auth'],
    postAuthReturnToPath: '/venue-terms-and-conditions',
    themeName: 'manualCSS',
    localeMessages: 'prismic',
  },
  {
    path: '/videos/:slug',
    componentPath: 'app/fan/components/pages/Video',
    localeMessages: 'fan',
    themeName: 'common',
  },
  {
    path: '/videos',
    componentPath: 'app/fan/components/pages/Videos',
    localeMessages: 'fan',
    themeName: 'common',
  },
  {
    path: '/visadestinations',
    componentPath: 'app/fan/components/pages/VisaGlobalOffer',
    themeName: 'common',
    localeMessages: 'fan',
  },
  {
    path: '/visadestinations/response',
    componentPath: 'app/fan/components/pages/VisaOfferRedirect',
    themeName: 'common',
    localeMessages: 'fan',
    iframeOnly: true,
  },
  {
    path: '/visaoffer',
    componentPath: 'app/fan/components/pages/VisaOffer',
    themeName: 'common',
    localeMessages: 'fan',
  },
  {
    path: '/visaoffer/response',
    componentPath: 'app/fan/components/pages/VisaOfferRedirect',
    themeName: 'common',
    localeMessages: 'fan',
    iframeOnly: true,
  },
  {
    path: '/sofar-sitemap',
    componentPath: 'app/fan/components/pages/SiteMap',
    themeName: 'common',
    localeMessages: 'fan',
  },
];

export const defaultRouteConfig: RouteInfo = {
  path: '*',
  componentPath: 'app/shared/components/pages/Prismic',
  themeName: 'manualCSS',
  localeMessages: 'prismic',
};
